import { Vector2 } from "../../common/vector2.js";
import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UICanvasRect } from "../common_ui_elements/ui_canvas_rect.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";

export interface AnchoredTooltipContent
{
  text: string;
  /** A short label (a reward name) in a small bubble sized to the text. */
  compact?: boolean;
  /** A touch on the anchor itself only closes the tooltip instead of reaching the anchor. */
  anchor_touch_closes?: boolean;
  note?: string;
  note_icon?: ImgID;
  /** Own content on the usual substrate and tail; `text` is then not drawn. */
  custom?: AnchoredTooltipCustomBody;
}

export interface AnchoredTooltipCustomBody
{
  design_w: number;
  design_h: number;
  /** Tail apex from the bubble's left edge (design px); the bubble then follows the anchor instead of centring on it. */
  tail_x?: number;
  tail_scale?: number;
  /** Extra vertical shift (design px), positive moves the bubble away from the anchor. */
  offset_y?: number;
  /** Own tail image pointing up (rotated when the bubble sits above the anchor), same 111x78 design box. */
  tail_img?: ImgID;
  /** How far the tail overlaps the substrate (design px, negative), default DESIGN_TAIL_Y_OFFSET. */
  tail_y_offset?: number;
  /** Adds the content to the substrate, `w`/`h` are its size in pixels. */
  build: ( ctx: CanvasRenderingContext2D, substrate: UIElement, w: number, h: number ) => void;
}

/** Anchor rectangle and bubble placement, all in root-local pixels. */
interface TooltipLayout
{
  x: number;
  y: number;
  w: number;
  h: number;
  visual_h: number;
  below: boolean;
  tail_x: number;
  tail_w: number;
  tail_h: number;
  tail_y_offset: number;
}

/**
 * Covers the root while a tooltip is open. Any touch closes the tooltip right
 * away and, unless it lands on a closing anchor, carries on to whatever is
 * underneath: a tap presses that button, a hold on another reward starts its
 * long press. Nothing has to be replayed afterwards.
 */
class TooltipDismissLayer extends UIElementHolder
{
  constructor( ctx: CanvasRenderingContext2D, w: number, h: number, private readonly on_touch: ( p: Vector2 ) => boolean )
  {
    super( ctx, 0, 0, w, h );
  }

  public override onTouchStart( p: Vector2, _now: number ): boolean
  {
    return this.on_touch( p );
  }

  public override name(): string { return "TooltipDismissLayer"; }
}

/**
 * A bubble with a tail pointing at an anchor element, above it or, when there
 * is no room, below. It lives in the anchor's root element (the dialog,
 * overlay or screen), so it works in any host. One is open at a time: show()
 * replaces it, and the next touch anywhere closes it.
 */
export class AnchoredTooltip
{
  private static readonly DESIGN_W = 720;
  private static readonly DESIGN_H = 200;
  private static readonly DESIGN_COMPACT_H = 150;
  private static readonly DESIGN_COMPACT_MIN_W = 320;
  /** Rough glyph width of the compact font, used to size the bubble to its text. */
  private static readonly DESIGN_COMPACT_CHAR_W = 30;
  private static readonly DESIGN_COMPACT_PAD_X = 60;
  private static readonly DESIGN_COMPACT_PAD_Y = 22;
  private static readonly COMPACT_TAIL_SCALE = 0.6;
  private static readonly DESIGN_SIDE_MARGIN = 30;
  private static readonly DESIGN_TEXT_PAD_X = 50;
  private static readonly DESIGN_TEXT_PAD_Y = 30;
  private static readonly DESIGN_TAIL_W = 111;
  private static readonly DESIGN_TAIL_H = 78;
  private static readonly DESIGN_TAIL_Y_OFFSET = -32;
  /** Negative: the narrow tail tip visually ends above its box, so it dips into the anchor's edge. */
  private static readonly DESIGN_ANCHOR_GAP = -16;
  private static readonly DESIGN_NOTE_W = 627;
  private static readonly DESIGN_NOTE_H = 224;
  private static readonly DESIGN_NOTE_TITLE_CENTER_Y = 62;
  private static readonly DESIGN_NOTE_TITLE_FONT = 45;
  private static readonly DESIGN_NOTE_DIVIDER_Y = 102;
  private static readonly DESIGN_NOTE_DIVIDER_W = 290;
  private static readonly DESIGN_NOTE_DIVIDER_H = 10;
  private static readonly DESIGN_NOTE_CENTER_Y = 144;
  private static readonly DESIGN_NOTE_FONT = 45;
  private static readonly DESIGN_NOTE_ICON = 59;
  private static readonly DESIGN_NOTE_ICON_GAP = 14;
  private static readonly DESIGN_NOTE_PAD_X = 40;

  private static opened: AnchoredTooltip | null = null;

  private readonly root: UIElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly anchor_x: number;
  private readonly anchor_y: number;
  private readonly anchor_w: number;
  private readonly anchor_h: number;
  private readonly dismiss_layer: TooltipDismissLayer;
  private readonly body: UIElementHolder;

  /** `owner` lets a feature hide only its own tooltip (see hide). */
  public static show( anchor: UIElement, content: AnchoredTooltipContent, owner: string | null = null ): void
  {
    AnchoredTooltip.hide();
    const tooltip = new AnchoredTooltip( anchor, content, owner );
    tooltip.attach();
    AnchoredTooltip.opened = tooltip;
  }

  /** Hides the open tooltip; with `owner`, only when that feature opened it. */
  public static hide( owner?: string ): void
  {
    const opened = AnchoredTooltip.opened;
    if ( !opened ) return;
    if ( owner !== undefined && opened.owner !== owner ) return;

    opened.close();
  }

  private constructor(
    anchor: UIElement,
    private readonly content: AnchoredTooltipContent,
    private readonly owner: string | null
  )
  {
    this.root = anchor.getRootElement();
    this.ctx = this.root.getContext();

    const root_rect = this.root.getAbsoluteRect();
    const anchor_rect = anchor.getAbsoluteRect();
    this.anchor_x = anchor_rect.x - root_rect.x;
    this.anchor_y = anchor_rect.y - root_rect.y;
    this.anchor_w = anchor_rect.width;
    this.anchor_h = anchor_rect.height;

    this.dismiss_layer = new TooltipDismissLayer(
      this.ctx,
      this.root.getRect().getW(),
      this.root.getRect().getH(),
      p => this.onDismissTouch( p )
    );
    this.body = this.createBody( this.computeLayout() );
  }

  private isCompact(): boolean
  {
    return this.content.compact === true && !this.hasNote() && !this.content.custom;
  }

  private hasNote(): boolean
  {
    return this.content.note !== undefined;
  }

  private computeLayout(): TooltipLayout
  {
    const T = AnchoredTooltip;
    const compact = this.isCompact();
    const note = this.hasNote();
    const custom = this.content.custom;
    const tail_scale = custom?.tail_scale ?? ( compact ? T.COMPACT_TAIL_SCALE : 1 );
    const w_design = custom ? custom.design_w
      : note ? T.DESIGN_NOTE_W
      : compact
        ? Math.min( T.DESIGN_W, Math.max( T.DESIGN_COMPACT_MIN_W, this.content.text.length * T.DESIGN_COMPACT_CHAR_W + T.DESIGN_COMPACT_PAD_X * 2 ) )
        : T.DESIGN_W;

    const w = LayoutScaler.widthFromDesign( w_design );
    const h = LayoutScaler.heightFromDesign( custom ? custom.design_h : note ? T.DESIGN_NOTE_H : compact ? T.DESIGN_COMPACT_H : T.DESIGN_H );
    const tail_w = LayoutScaler.widthFromDesign( T.DESIGN_TAIL_W * tail_scale );
    const tail_h = LayoutScaler.heightFromDesign( T.DESIGN_TAIL_H * tail_scale );
    const tail_y_offset = LayoutScaler.heightFromDesign( ( custom?.tail_y_offset ?? T.DESIGN_TAIL_Y_OFFSET ) * tail_scale );
    const side_margin = LayoutScaler.widthFromDesign( T.DESIGN_SIDE_MARGIN );
    const anchor_gap = LayoutScaler.heightFromDesign( T.DESIGN_ANCHOR_GAP );
    const root_w = this.root.getRect().getW();
    const root_h = this.root.getRect().getH();

    const anchor_center_x = this.anchor_x + this.anchor_w / 2;
    const anchor_bottom = this.anchor_y + this.anchor_h;

    const x = custom?.tail_x !== undefined
      ? Math.min( anchor_center_x - LayoutScaler.widthFromDesign( custom.tail_x ), root_w - w )
      : Math.max( side_margin, Math.min( anchor_center_x - w / 2, root_w - side_margin - w ) );
    const visual_h = h + tail_y_offset + tail_h;
    const offset_y = LayoutScaler.heightFromDesign( custom?.offset_y ?? 0 );
    const above_y = this.anchor_y - anchor_gap - offset_y - visual_h;
    const below = above_y < 0 && anchor_bottom + anchor_gap + offset_y + visual_h <= root_h;
    const y = below ? anchor_bottom + anchor_gap + offset_y : Math.max( 0, above_y );
    const tail_x = Math.max( 0, Math.min( anchor_center_x - x - tail_w / 2, w - tail_w ) );

    return { x, y, w, h, visual_h, below, tail_x, tail_w, tail_h, tail_y_offset };
  }

  private createBody( layout: TooltipLayout ): UIElementHolder
  {
    const { x, y, w, h, visual_h, below, tail_x, tail_w, tail_h, tail_y_offset } = layout;

    const body = new UIElementHolder( this.ctx, x, y, w, visual_h );
    body.setClickable( false );

    // Below the anchor the tail sits on top and the substrate moves down under it.
    const substrate_y = below ? visual_h - h : 0;
    const substrate = new UINineSliceElement( this.ctx, 0, substrate_y, w, h, ImgID.UI_TutorialSubstrate, "tutorial_substrate" );
    body.addChild( substrate, true );
    if ( this.content.custom )
      this.content.custom.build( this.ctx, substrate, w, h );
    else if ( this.hasNote() )
      this.addNoteContent( substrate, w, h );
    else
      substrate.addChild( this.isCompact() ? this.createCompactText( w, h ) : this.createPlainText( w, h ), true );

    // UI_TooltipArrow points down, a custom tail_img points up.
    const tail_img = this.content.custom?.tail_img;
    const flip = tail_img === undefined ? below : !below;
    const tail = new UIImage(
      this.ctx,
      tail_x,
      below ? 0 : h + tail_y_offset,
      tail_w,
      tail_h,
      tail_img ?? ImgID.UI_TooltipArrow,
      flip ? { rotation_angle: 180 } : undefined
    );
    body.addChild( tail, true );

    return body;
  }

  private createPlainText( w: number, h: number ): UITextElement
  {
    const pad_x = LayoutScaler.widthFromDesign( AnchoredTooltip.DESIGN_TEXT_PAD_X );
    const pad_y = LayoutScaler.heightFromDesign( AnchoredTooltip.DESIGN_TEXT_PAD_Y );
    return new UITextElement( this.ctx, pad_x, pad_y, w - pad_x * 2, h - pad_y * 2, {
      text: this.content.text,
      font_size: LayoutScaler.textSizeFromDesign( 44 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_color: ColorScheme.continue_overlay_caption_outline,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 26 ),
      line_height: 0.95,
      auto_wrap: true,
    } );
  }

  private addNoteContent( substrate: UINineSliceElement, w: number, h: number ): void
  {
    const T = AnchoredTooltip;
    const pad_x = LayoutScaler.widthFromDesign( T.DESIGN_NOTE_PAD_X );
    const title_h = LayoutScaler.heightFromDesign( T.DESIGN_NOTE_TITLE_FONT * 1.6 );

    substrate.addChild( this.noteText(
      this.content.text,
      pad_x,
      LayoutScaler.yFromDesign( T.DESIGN_NOTE_TITLE_CENTER_Y ) - title_h / 2,
      w - pad_x * 2,
      title_h,
      T.DESIGN_NOTE_TITLE_FONT
    ), true );

    const divider_w = LayoutScaler.widthFromDesign( T.DESIGN_NOTE_DIVIDER_W );
    const divider_h = LayoutScaler.heightFromDesign( T.DESIGN_NOTE_DIVIDER_H );
    substrate.addChild( new UICanvasRect(
      this.ctx,
      ( w - divider_w ) / 2,
      LayoutScaler.yFromDesign( T.DESIGN_NOTE_DIVIDER_Y ),
      divider_w,
      divider_h,
      ColorScheme.anchored_tooltip_divider,
      divider_h / 2
    ), true );

    const note_h = LayoutScaler.heightFromDesign( T.DESIGN_NOTE_FONT * 1.6 );
    const note_y = LayoutScaler.yFromDesign( T.DESIGN_NOTE_CENTER_Y ) - note_h / 2;
    const note = this.noteText( this.content.note ?? "", pad_x, note_y, w - pad_x * 2, note_h, T.DESIGN_NOTE_FONT );

    const icon_id = this.content.note_icon;
    if ( icon_id === undefined )
    {
      substrate.addChild( note, true );
      return;
    }

    const icon_w = LayoutScaler.widthFromDesign( T.DESIGN_NOTE_ICON );
    const gap = LayoutScaler.widthFromDesign( T.DESIGN_NOTE_ICON_GAP );
    const text_w = Math.min( w - pad_x * 2 - gap - icon_w, note.getNaturalTextWidth() );
    const line_x = ( w - ( text_w + gap + icon_w ) ) / 2;

    note.setPixelPos( new Vector2( line_x, note_y ), true );
    note.getRect().resize( text_w, note_h );
    substrate.addChild( note, true );
    substrate.addChild( new UIImage(
      this.ctx,
      line_x + text_w + gap,
      LayoutScaler.yFromDesign( T.DESIGN_NOTE_CENTER_Y ) - icon_w / 2,
      icon_w,
      LayoutScaler.heightFromDesign( T.DESIGN_NOTE_ICON ),
      icon_id
    ), true );
  }

  private noteText( text: string, x: number, y: number, w: number, h: number, font_size: number ): UITextElement
  {
    return new UITextElement( this.ctx, x, y, w, h, {
      text,
      font_size: LayoutScaler.textSizeFromDesign( font_size ),
      font_weight: 900,
      text_color: ColorScheme.anchored_tooltip_text,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: 0,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( Math.round( font_size * 0.6 ) ),
      auto_wrap: false,
    } );
  }

  private createCompactText( w: number, h: number ): UITextElement
  {
    const pad_x = LayoutScaler.widthFromDesign( AnchoredTooltip.DESIGN_COMPACT_PAD_X );
    const pad_y = LayoutScaler.heightFromDesign( AnchoredTooltip.DESIGN_COMPACT_PAD_Y );
    return new UITextElement( this.ctx, pad_x, pad_y, w - pad_x * 2, h - pad_y * 2, {
      text: this.content.text,
      font_size: LayoutScaler.textSizeFromDesign( 46 ),
      font_weight: 900,
      text_color: ColorScheme.anchored_tooltip_text,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: 0,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 28 ),
      auto_wrap: false,
    } );
  }

  private onDismissTouch( p: Vector2 ): boolean
  {
    this.close();
    const on_anchor = p.x >= this.anchor_x && p.x <= this.anchor_x + this.anchor_w
      && p.y >= this.anchor_y && p.y <= this.anchor_y + this.anchor_h;
    return this.content.anchor_touch_closes === true && on_anchor;
  }

  private attach(): void
  {
    this.addToRoot( this.dismiss_layer );
    this.addToRoot( this.body );
    this.root.invalidateSubtree();
  }

  private close(): void
  {
    if ( AnchoredTooltip.opened === this )
      AnchoredTooltip.opened = null;

    this.removeFromRoot( this.dismiss_layer );
    this.removeFromRoot( this.body );
    this.body.cleanup();
    this.root.invalidateSubtree();
  }

  // The tooltip overlays the whole root, and its coordinates are root-relative.
  // Some roots override addChild to redirect children into an inner container
  // (DailyTasksScreen puts them in content_root), which shifted the tooltip and
  // left removeChild unable to find it, so the base methods are called directly.
  private addToRoot( element: UIElement ): void
  {
    UIElement.prototype.addChild.call( this.root, element );
  }

  private removeFromRoot( element: UIElement ): void
  {
    UIElement.prototype.removeChild.call( this.root, element );
  }
}
